// 📁 WaitingReviewList.jsx — Chuẩn UI mới
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Clock,
  Calendar,
  Timer,
  CheckCircle2,
  FileText,
  Upload,
  Trash2,
  Loader2,
  Send,
  ShieldCheck,
  XCircle,
  ExternalLink,
} from "lucide-react";
import defaultAvatar from "../../../assets/images/avatar/employee-1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faHourglassHalf,
  faXmark,
  faStar,
  faNoteSticky,
  faMailBulk,
} from "@fortawesome/free-solid-svg-icons";
import { reviewOvertime } from "../../../api/hrm/employees/overtimes";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { getWaitingReviewOvertimes } from "../../../api/hrm/employees/overtimes";
import { formatDateVN } from "../../../utils/formatUtils";

export default function WaitingReviewList({
  filters,
  refreshFlag,
  onChanged,
  autoOpenId = null,
  onAutoOpened,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewNote, setReviewNote] = useState(""); // ⭐ State nội dung đánh giá
  const [reviewScore, setReviewScore] = useState(0); // ⭐ State điểm đánh giá

  const runIdRef = useRef(`run_wrr_post_${Date.now()}`);
  const reqSeqRef = useRef(0);
  const latestReqIdRef = useRef(0);
  const autoOpenedRef = useRef(null);

  // Lấy thông tin user hiện tại từ localStorage
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = storedUser?.id;
  const canReview = currentUserId === selected?.level1; // Người đánh giá là người duyệt cấp 1

  // Nếu muốn cho cả cấp 2 đánh giá, bỏ comment đoạn dưới
  // const canReview =
  //   currentUserId === selected?.level1 ||
  //   currentUserId === selected?.level2;

  const loadData = async () => {
    if (!filters?.from_date || !filters?.to_date) return;

    const reqId = ++reqSeqRef.current;
    latestReqIdRef.current = reqId;

    setLoading(true);

    // #region agent log fetchData start
    fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "0dfe12",
      },
      body: JSON.stringify({
        sessionId: "0dfe12",
        runId: runIdRef.current,
        hypothesisId: "H4_waitingReview_filter_stale_or_unready",
        location: "src/modules/hrm/overtime/WaitingReviewList.jsx:loadData:start",
        message: "OT waiting review load start",
        data: {
          reqId,
          pageFrom: filters.from_date,
          pageTo: filters.to_date,
          keyword: filters.keyword,
          branch_id: filters.branch_id,
          department_id: filters.department_id,
          status: filters.status,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    try {
      const res = await getWaitingReviewOvertimes(filters);
      // #region agent log fetchData response
      fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Debug-Session-Id": "0dfe12",
        },
        body: JSON.stringify({
          sessionId: "0dfe12",
          runId: runIdRef.current,
          hypothesisId: "H4_waitingReview_filter_stale_or_unready",
          location:
            "src/modules/hrm/overtime/WaitingReviewList.jsx:loadData:response",
          message: "OT waiting review load response",
          data: {
            reqId,
            latestReqIdAtResponse: latestReqIdRef.current,
            recordsCount: Array.isArray(res) ? res.length : 0,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion

      if (reqId !== latestReqIdRef.current) return;
      setData(res || []);
    } catch (err) {
      console.error("❌ Lỗi load waiting review:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [filters, refreshFlag]); // 👈 load lại nếu đổi filter hoặc refreshFlag

  useEffect(() => {
    if (!autoOpenId || autoOpenedRef.current === autoOpenId) return;
    const target = data.find((item) => String(item.id) === String(autoOpenId));
    if (!target) return;

    autoOpenedRef.current = autoOpenId;
    setSelected(target);
    setReviewNote("");
    setReviewScore(0);
    onAutoOpened?.(target);
  }, [autoOpenId, data]);

  if (!loading && data.length === 0) {
    return null;
  }

  // ⭐ Hàm gửi đánh giá cuối
  const handleFinalReview = async (approved) => {
    try {
      if (!selected) return;

      await reviewOvertime(selected.id, {
        review_note: reviewNote || "",
        review_score: reviewScore,
      });

      showSuccessToast("Đã đánh giá phiếu tăng ca");

      setSelected(null);
      loadData(); // 🔥 Load lại danh sách
      onChanged?.();
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể gửi đánh giá");
    }
  };

  return (
    <div className="mt-8">
      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
        Phiếu chờ đánh giá cuối ({data.length} phiếu)
      </h2>

      {/* LIST CARD */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}      
      <div className="grid grid-cols-1 mb:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {data.map((item) => {
          const hours = (item.ot_minutes / 60).toFixed(1);
          const avatar = item.employee_avatar || defaultAvatar;
          const isManager = storedUser?.id === item.level1;
          const isOwner = storedUser?.id === item.employee_id;

          return (
            <div
              key={item.id}
              onClick={() => {
                setSelected(item);
                setReviewNote("");
                setReviewScore(0);
              }}
              className="group relative bg-white rounded-[1.8rem] p-4 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* ⭐ Tags Trạng thái thu gọn */}
              {isManager ? (
                <div className="absolute top-0 right-0 bg-red-600 text-white px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-bl-xl shadow-sm">
                  Cần duyệt
                </div>
              ) : (
                isOwner && (
                  <div className="absolute top-0 right-0 bg-sky-600 text-white px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-bl-xl shadow-sm">
                    Chờ đánh giá
                  </div>
                )
              )}

              {/* ROW 1: Avatar + Name */}
              <div className="flex gap-3 items-center mb-3">
                <div className="relative shrink-0">
                  <img
                    src={avatar}
                    className="w-10 h-10 rounded-xl object-cover border border-gray-50 shadow-sm group-hover:scale-105 transition-transform"
                    alt="avatar"
                  />
                  <div
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                      isManager ? "bg-red-500 animate-pulse" : "bg-sky-400"
                    }`}
                  />
                </div>

                <div className="flex flex-col min-w-0 text-left">
                  <p className="font-black text-gray-800 text-[13px] leading-tight truncate uppercase tracking-tight">
                    {item.employee_name}
                  </p>
                  <p className="text-[10px] font-bold text-gray-400 italic truncate mt-0.5">
                    {item.branch_name}
                  </p>
                </div>
              </div>

              {/* ROW 2: Badge Giờ & Ngày (Dàn hàng ngang) */}
              <div className="flex items-center justify-between mb-3 pt-3 border-t border-gray-50">
                <span className="px-2 py-0.5 text-[9px] font-black rounded-lg bg-sky-50 text-sky-600 border border-sky-100 uppercase tracking-widest shrink-0">
                  {hours}H OT
                </span>
                <div className="flex items-center text-[11px] font-bold text-gray-500 gap-1.5">
                  <Calendar className="text-gray-300" size={12} />
                  {formatDateVN(item.work_date)}
                </div>
              </div>

              {/* ROW 3: Khoảng thời gian */}
              <div className="flex items-center text-[11px] font-bold text-gray-600 gap-2 mb-3">
                <div className="w-5 h-5 rounded-md bg-slate-50 flex items-center justify-center text-slate-400">
                  <Clock size={10} />
                </div>
                <span className="font-black tracking-tighter">
                  {item.ot_from?.slice(0, 5)} — {item.ot_to?.slice(0, 5)}
                </span>
              </div>

              {/* ROW 4: Lý do & Báo cáo (Gộp khối) */}
              <div className="space-y-2">
                {/* Lý do ban đầu */}
                <div className="px-2.5 py-2 bg-gray-50/80 rounded-xl border border-gray-100/50">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                    <FileText size={10} /> Yêu cầu
                  </p>
                  <p className="text-[11px] text-gray-600 line-clamp-1 font-bold italic">
                    "{item.reason}"
                  </p>
                </div>

                {/* Nội dung báo cáo thực tế */}
                <div className="px-2.5 py-2 bg-emerald-50/50 rounded-xl border border-emerald-100/50 group-hover:bg-emerald-50 transition-colors">
                  <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1 flex items-center gap-1">
                    <CheckCircle2 size={10} /> Báo cáo
                  </p>
                  <p className="text-[11px] text-emerald-700 line-clamp-1 font-bold">
                    {item.report_content || "Chưa có nội dung"}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ POPUP ĐÁNH GIÁ NÂNG CẤP */}
      <AnimatePresence>
        {selected && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-[650px] max-w-[95%] overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
            >
              {/* HEADER */}
              <div className="p-8 pb-4 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm border border-blue-100">
                    <ShieldCheck size={24} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h2 className="text-xl font-black uppercase tracking-tight text-gray-800">
                      Đánh giá báo cáo
                    </h2>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5 italic">
                      Xác nhận kết quả tăng ca hệ thống
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-2xl transition-all border border-gray-100"
                >
                  <X size={20} strokeWidth={2.5} />
                </button>
              </div>

              {/* CONTENT */}
              <div className="px-8 pb-8 space-y-6 overflow-y-auto custom-scrollbar">
                {/* Employee & Status Summary */}
                <div className="flex items-center gap-5 p-5 bg-slate-50 rounded-[2rem] border border-slate-100">
                  <img
                    src={selected.employee_avatar || defaultAvatar}
                    className="w-16 h-16 rounded-[1.5rem] object-cover border-2 border-white shadow-md"
                    alt="avatar"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-black text-gray-800 uppercase tracking-tight leading-tight truncate">
                      {selected.employee_name}
                    </h3>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                      {selected.branch_name} • {selected.department_name}
                    </p>
                    <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-50 border border-blue-100 text-[9px] font-black text-blue-600 uppercase tracking-widest">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      Chờ đánh giá cuối
                    </div>
                  </div>
                </div>

                {/* Work & Report Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-white border border-gray-50 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Calendar size={16} className="text-slate-400" />
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          Ngày làm việc
                        </span>
                      </div>
                      <span className="text-sm font-black text-gray-700">
                        {formatDateVN(selected.work_date)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white border border-gray-50 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Timer size={16} className="text-emerald-500" />
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          Tổng thời gian
                        </span>
                      </div>
                      <span className="text-sm font-black text-emerald-600">
                        {(selected.ot_minutes / 60).toFixed(2)} giờ
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-50/30 border border-amber-100/50 rounded-2xl space-y-2">
                    <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest flex items-center gap-2">
                      <FileText size={14} /> Lý do yêu cầu
                    </p>
                    <p className="text-xs text-amber-900 font-bold italic leading-relaxed line-clamp-3">
                      "{selected.reason}"
                    </p>
                  </div>
                </div>

                {/* Report Text & Files */}
                <div className="space-y-3">
                  <div className="p-5 bg-blue-50/30 border border-blue-100/50 rounded-[1.5rem] space-y-3">
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-2">
                      <Send size={14} /> Kết quả báo cáo thực tế
                    </p>
                    <p className="text-sm text-slate-700 font-bold leading-relaxed italic">
                      "{selected.report_content || "Không có nội dung"}"
                    </p>

                    {/* File Viewer Section */}
                    {selected.report_files?.length > 0 &&
                      (() => {
                        const fileUrl = selected.report_files[0];
                        const cleanName = fileUrl
                          .split("/")
                          .pop()
                          .split("?")[0];
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(
                          cleanName
                        );

                        return (
                          <div className="mt-4 flex items-center gap-4 p-3 bg-white rounded-2xl border border-blue-100 shadow-sm">
                            <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                              {isImage ? (
                                <img
                                  src={fileUrl}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="text-red-600 font-black text-[10px] uppercase">
                                  PDF
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-black text-slate-700 text-xs truncate">
                                {cleanName}
                              </div>
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[10px] font-black text-blue-600 uppercase mt-1 tracking-widest hover:underline"
                              >
                                <ExternalLink size={10} /> Mở tệp đính kèm
                              </a>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                </div>

                {/* REVIEW FORM */}
                {canReview && (
                  <div className="pt-4 border-t border-gray-100 space-y-5">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">
                        Nhận xét của quản lý
                      </label>
                      <textarea
                        className="w-full bg-white border border-gray-200 rounded-[1.5rem] p-4 font-bold text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-200 transition-all shadow-sm min-h-[90px] placeholder:font-medium placeholder:text-gray-300"
                        placeholder="Nhập nhận xét đánh giá..."
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        Chấm điểm chất lượng
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FontAwesomeIcon
                              key={star}
                              icon={faStar}
                              className={`cursor-pointer text-lg transition-all active:scale-125 ${
                                reviewScore >= star
                                  ? "text-yellow-500 drop-shadow-sm"
                                  : "text-slate-200"
                              }`}
                              onClick={() => setReviewScore(star)}
                            />
                          ))}
                        </div>
                        <span className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-slate-700 text-sm shadow-sm">
                          {reviewScore}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER ACTIONS */}
              {canReview && (
                <div className="p-8 pt-0 flex flex-col sm:flex-row gap-3 bg-white">
                  <button
                    onClick={() => handleFinalReview(false)}
                    className="flex-1 px-6 py-4 border border-rose-100 text-rose-600 rounded-2xl font-black text-[11px] uppercase tracking-widest bg-rose-50 hover:bg-rose-100 transition-all flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} /> Không duyệt
                  </button>
                  <button
                    onClick={() => handleFinalReview(true)}
                    className="flex-2 px-10 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] uppercase tracking-[0.15em] rounded-2xl shadow-lg shadow-emerald-100 transition-all active:scale-95 flex items-center justify-center gap-2 shrink-0"
                  >
                    <CheckCircle2 size={16} /> Duyệt báo cáo & Lưu điểm
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
